import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { adminApi, can, type CodeItem } from '../lib/adminApi'

/**
 * 공통코드 라벨·순서 — 화면 선택지(분야·대상·자원 …)의 **이름과 차례**를 배포 없이 바꾼다.
 *
 * 여기까지 오게 된 경위: 등록 화면의 라벨은 원래 서버 `common_code`가 정본인데(설계 A-5)
 * 정작 바꿀 화면이 없어서 마이그레이션을 새로 쓰거나 DB를 직접 UPDATE 해야 했다. 후자는
 * `audit_log`가 안 남는다 — 09-02에 개발 스위치 값이 누가 왜 되돌렸는지 모르게 된 그 함정이다.
 *
 * **코드 추가·삭제·활성 토글은 없다.** 코드값의 도달점은 시드이고(CLAUDE.md §4-10),
 * 폐기 판정은 근거 있는 결정이다(022는 0821 이티랩 확정본을 근거로 `res_network`를 내렸다).
 * 화면에서 그걸 뒤집으면 근거가 사라진다 — 그런 변경은 마이그레이션으로 낸다.
 */

/** 그룹 코드에 사람이 읽는 이름을 붙인다. 목록에 없으면 코드 그대로 보여준다. */
const GROUP_LABEL: Record<string, string> = {
  category: '분야(세부)',
  category_group: '분야(대분류)',
  target_group: '주 대상(세부)',
  target_lifecycle: '주 대상(생애주기)',
  target_situation: '주 대상(상황)',
  resource: '자원',
  service_area: '서비스 지역',
  org_type: '기업 유형',
  cost_type: '비용 유형',
  cert_type: '인증 유형',
  desired_period: '희망 기간',
  impact_scope: '파급 범위',
  intake_channel: '유입 경로',
  operation_status: '운영 상태',
  pilot_available: '시범 가능 여부',
  review_status: '검수 상태',
  urgency: '시급성',
  help_type: '도움 방식(폐기)',
}

function groupLabel(group: string): string {
  return GROUP_LABEL[group] ?? group
}

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminCodesPage() {
  const me = useAdminMe()
  const mayManage = can(me.data, 'admin', 'manage')
  // 어느 그룹을 보고 있는지는 URL에 둔다 — 저장하고 새로고침해도 같은 화면으로 돌아온다.
  const [params, setParams] = useSearchParams()
  const group = params.get('group') ?? 'resource'

  const groups = useQuery({ queryKey: ['admin-code-groups'], queryFn: () => adminApi.codeGroups() })

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">코드·라벨</h1>
          <p className="page-desc">
            등록 화면과 상세 화면에 뜨는 선택지의 <strong>이름과 순서</strong>를 바꿉니다. 코드값
            자체는 바꾸지 않으므로 이미 저장된 카드에는 영향이 없습니다 — 보이는 이름만 바뀝니다.
          </p>
          <p className="muted small">
            항목을 늘리거나 없애는 것은 여기서 하지 않습니다(구축 담당자 작업). 폐기된 항목은
            회색으로 보이고 등록 화면에는 뜨지 않습니다. <Link to="/admin/settings">설정으로</Link>
          </p>
        </div>
      </div>

      <section className="card-block">
        <div className="form-row">
          <label htmlFor="code-group">코드 그룹</label>
          <QueryState isPending={groups.isPending} error={groups.error} isEmpty={false}>
            {groups.data !== undefined && (
              <select
                id="code-group"
                value={group}
                onChange={(e) => setParams({ group: e.target.value })}
              >
                {groups.data.items.map((g) => (
                  <option key={g.group} value={g.group}>
                    {groupLabel(g.group)} ({g.active}
                    {g.total !== g.active ? ` / 폐기 ${g.total - g.active}` : ''})
                  </option>
                ))}
              </select>
            )}
          </QueryState>
        </div>
      </section>

      <CodeGroupEditor key={group} group={group} mayManage={mayManage} />
    </div>
  )
}

function CodeGroupEditor(props: { group: string; mayManage: boolean }) {
  const { group, mayManage } = props
  const queryClient = useQueryClient()
  const detail = useQuery({
    queryKey: ['admin-code-group', group],
    queryFn: () => adminApi.codeGroup(group),
  })
  // null = 아직 손 안 댐. 저장 후 비워서 서버가 준 값을 다시 그린다.
  const [draft, setDraft] = useState<CodeItem[] | null>(null)

  const save = useMutation({
    mutationFn: (items: CodeItem[]) =>
      adminApi.setCodeGroup(
        group,
        items.map((i) => ({ code: i.code, label: i.label })),
      ),
    onSuccess: () => {
      setDraft(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-code-group', group] })
      // 공개 화면이 쓰는 코드 캐시도 비운다 — 안 그러면 바꾼 라벨이 같은 탭의 등록 화면에
      // 최대 10분 늦게 닿는다(codes.ts staleTime).
      void queryClient.invalidateQueries({ queryKey: ['common-codes'] })
    },
  })

  const current = draft ?? detail.data?.items ?? null
  const changed =
    current !== null &&
    detail.data !== undefined &&
    current.some((it, i) => {
      const orig = detail.data.items[i]
      return orig === undefined || orig.code !== it.code || orig.label !== it.label
    })

  const setLabel = (code: string, label: string): void => {
    if (current === null) return
    setDraft(current.map((it) => (it.code === code ? { ...it, label } : it)))
  }

  /**
   * 위/아래 이동은 **같은 활성 상태끼리만** 자리를 바꾼다. 폐기 항목을 활성 사이로 끌어올려도
   * 서버가 다시 뒤로 보내므로(활성 먼저 1..N), 화면에서 되지 않는 동작을 되는 것처럼
   * 보여주지 않는다.
   */
  const move = (index: number, delta: number): void => {
    if (current === null) return
    const target = index + delta
    const a = current[index]
    const b = current[target]
    if (a === undefined || b === undefined || a.isActive !== b.isActive) return
    const next = [...current]
    next[index] = b
    next[target] = a
    setDraft(next)
  }

  const invalid =
    current !== null && current.some((it) => it.label.trim().length === 0 || it.label.length > 60)

  return (
    <section className="card-block">
      <h2>{groupLabel(group)}</h2>
      <QueryState isPending={detail.isPending} error={detail.error} isEmpty={false}>
        {current !== null && (
          <>
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: '6rem' }}>순서</th>
                  <th>이름 (화면에 보이는 말)</th>
                  <th style={{ width: '14rem' }}>코드값</th>
                </tr>
              </thead>
              <tbody>
                {current.map((item, i) => (
                  <tr key={item.code} className={item.isActive ? undefined : 'muted'}>
                    <td>
                      <button
                        type="button"
                        className="btn ghost small-btn"
                        aria-label={`${item.label} 위로`}
                        disabled={!mayManage || i === 0 || current[i - 1]?.isActive !== item.isActive}
                        onClick={() => move(i, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="btn ghost small-btn"
                        aria-label={`${item.label} 아래로`}
                        disabled={
                          !mayManage ||
                          i === current.length - 1 ||
                          current[i + 1]?.isActive !== item.isActive
                        }
                        onClick={() => move(i, 1)}
                      >
                        ↓
                      </button>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={item.label}
                        maxLength={60}
                        disabled={!mayManage}
                        aria-label={`${item.code} 이름`}
                        onChange={(e) => setLabel(item.code, e.target.value)}
                      />
                      {!item.isActive && <span className="muted small"> · 폐기(화면에 안 뜸)</span>}
                      {item.note !== null && <p className="muted small">{item.note}</p>}
                    </td>
                    <td>
                      <code>{item.code}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {mayManage ? (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn"
                  disabled={!changed || invalid || save.isPending}
                  onClick={() => save.mutate(current)}
                >
                  저장
                </button>
                <button
                  type="button"
                  className="btn ghost"
                  disabled={!changed || save.isPending}
                  onClick={() => setDraft(null)}
                >
                  되돌리기
                </button>
                {invalid && <span className="error">이름은 비워 둘 수 없습니다.</span>}
              </div>
            ) : (
              <p className="muted small">변경은 관리 권한(admin:manage) 계정만 할 수 있습니다.</p>
            )}

            {save.error !== null && (
              <p className="error" role="alert">
                {errorMessage(save.error)}
              </p>
            )}
            {save.isSuccess && !changed && (
              <p className="ok" role="status">
                저장했습니다. 등록 화면에 바로 반영됩니다.
              </p>
            )}
          </>
        )}
      </QueryState>
    </section>
  )
}
